import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FiActivity, FiRefreshCw, FiDatabase, FiServer, FiPhoneCall } from 'react-icons/fi';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const AdminHealth = () => {
    const [twilioHealth, setTwilioHealth] = useState(null);
    const [dbHealth, setDbHealth] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchHealthData = async () => {
        setLoading(true);
        setError(null);
        try {
            const token = localStorage.getItem('adminToken') || localStorage.getItem('token');
            const headers = { Authorization: `Bearer ${token}` };

            const [twilioRes, dbRes] = await Promise.all([
                axios.get(`${API_URL}/twilio/health`, { headers }).catch(e => e.response || { data: { status: 'error', message: e.message } }),
                axios.get(`${API_URL}/db-status`, { headers }).catch(e => e.response || { data: { status: 'error', message: e.message } })
            ]);

            setTwilioHealth(twilioRes.data);
            setDbHealth(dbRes.data);
        } catch (err) {
            console.error('Failed to fetch health data:', err);
            setError('Failed to load system health data.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchHealthData();
    }, []);

    const renderStatusBadge = (status) => {
        const isOk = status === 'ok' || status === 'connected';
        return (
            <span style={{
                padding: '4px 12px',
                borderRadius: '20px',
                fontSize: '0.8rem',
                fontWeight: '600',
                background: isOk ? 'rgba(34, 197, 94, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                color: isOk ? '#22c55e' : '#ef4444',
                textTransform: 'uppercase'
            }}>
                {status || 'unknown'}
            </span>
        );
    };

    if (loading) {
        return (
            <div style={{ padding: '2rem', color: 'var(--text-muted)' }}>
                <FiRefreshCw className="spinner" style={{ marginRight: '10px' }} /> Checking system health...
            </div>
        );
    }

    if (error) {
        return (
            <div style={{ padding: '2rem', color: 'var(--danger)' }}>
                {error}
                <br /><br />
                <button onClick={fetchHealthData} className="btn-primary">Retry</button>
            </div>
        );
    }

    return (
        <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
                <h1 style={{ fontSize: '1.75rem', display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <FiActivity color="var(--primary)" /> System Health
                </h1>
                <button
                    onClick={fetchHealthData}
                    style={{
                        background: 'rgba(255,255,255,0.05)', border: '1px solid var(--border)',
                        color: 'white', padding: '8px 16px', borderRadius: '8px', cursor: 'pointer',
                        display: 'flex', alignItems: 'center', gap: '8px'
                    }}
                >
                    <FiRefreshCw /> Refresh Status
                </button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1.5rem' }}>

                {/* Database Health Card */}
                <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: '16px', padding: '1.5rem' }}>
                    <h3 style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '1.25rem', marginBottom: '1rem', borderBottom: '1px solid var(--border)', paddingBottom: '1rem' }}>
                        <FiDatabase color="#3b82f6" /> Database Status
                    </h3>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '1rem' }}>
                        <span style={{ color: 'var(--text-muted)' }}>Connection State:</span>
                        {renderStatusBadge(dbHealth?.currentState)}
                    </div>
                </div>

                {/* Twilio API Health Card */}
                <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: '16px', padding: '1.5rem' }}>
                    <h3 style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '1.25rem', marginBottom: '1rem', borderBottom: '1px solid var(--border)', paddingBottom: '1rem' }}>
                        <FiPhoneCall color="#e11d48" /> Twilio Connectivty
                    </h3>

                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '1rem' }}>
                        <span style={{ color: 'var(--text-muted)' }}>API Status:</span>
                        {renderStatusBadge(twilioHealth?.checks?.twilio_api?.status)}
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '1rem' }}>
                        <span style={{ color: 'var(--text-muted)' }}>Environment Vars:</span>
                        {renderStatusBadge(twilioHealth?.checks?.env?.status)}
                    </div>

                    {twilioHealth?.checks?.twilio_api?.status === 'ok' && (
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.9rem' }}>
                            <span style={{ color: 'var(--text-muted)' }}>Account Name:</span>
                            <span style={{ color: 'white' }}>{twilioHealth.checks.twilio_api.friendly_name}</span>
                        </div>
                    )}

                    {twilioHealth?.checks?.env?.missing?.length > 0 && (
                        <div style={{ marginTop: '1rem', padding: '1rem', background: 'rgba(239, 68, 68, 0.1)', borderRadius: '8px', border: '1px solid rgba(239, 68, 68, 0.2)' }}>
                            <span style={{ color: 'var(--danger)', fontSize: '0.9rem', fontWeight: 'bold' }}>Missing Variables:</span>
                            <ul style={{ margin: '0.5rem 0 0', paddingLeft: '1.5rem', color: '#fca5a5', fontSize: '0.85rem' }}>
                                {twilioHealth.checks.env.missing.map(env => <li key={env}>{env}</li>)}
                            </ul>
                        </div>
                    )}
                </div>

                {/* Webhook Configuration Reminders */}
                <div style={{ gridColumn: '1 / -1', background: 'rgba(59, 130, 246, 0.05)', border: '1px solid rgba(59, 130, 246, 0.2)', borderRadius: '16px', padding: '1.5rem', marginTop: '1rem' }}>
                    <h3 style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '1.25rem', marginBottom: '1rem', color: '#60a5fa' }}>
                        <FiServer /> Webhook Configuration Guide
                    </h3>
                    <p style={{ color: 'var(--text-muted)', fontSize: '0.95rem', lineHeight: '1.6', marginBottom: '1rem' }}>
                        To ensure billing works correctly, your Twilio Phone Number and TwiML App must be configured to point to these exact URLs for this environment:
                    </p>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                            <strong style={{ color: 'white', fontSize: '0.9rem' }}>Status Callback URL (Tracking Call Duration):</strong>
                            <code style={{ background: 'rgba(0,0,0,0.3)', padding: '8px 12px', borderRadius: '6px', fontSize: '0.85rem', color: '#a78bfa', userSelect: 'all' }}>
                                {API_URL.replace('/api', '')}/api/calls/webhook/status
                            </code>
                        </div>
                    </div>
                </div>

            </div>
        </div>
    );
};

export default AdminHealth;
